<template>
  <div class="system-container">
    <!-- Tab 导航 -->
    <div class="tab-container">
      <div class="tab-item" :class="{ active: activeTab === 'account' }" @click="activeTab = 'account'">
        <span class="tab-icon">👤</span>
        <span class="tab-text">账号管理</span>
      </div>
      <div class="tab-item" :class="{ active: activeTab === 'role' }" @click="activeTab = 'role'">
        <span class="tab-icon">🎭</span>
        <span class="tab-text">角色管理</span>
      </div>
      <div class="tab-item" :class="{ active: activeTab === 'log' }" @click="activeTab = 'log'">
        <span class="tab-icon">📋</span>
        <span class="tab-text">操作日志</span>
      </div>
    </div>

    <!-- Tab 内容 -->
    <div class="tab-content">
      <!-- 账号管理 -->
      <div v-show="activeTab === 'account'" class="tab-panel">
        <div class="panel-header">
          <div class="panel-title">
            <span class="title-icon">👤</span>
            <h2>账号管理</h2>
          </div>
          <button class="btn-add" @click="showAddAccountModal = true">
            <span class="btn-icon">+</span>
            <span>添加账号</span>
          </button>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">ID</th>
                <th style="width: 120px;">用户名</th>
                <th style="width: 120px;">角色</th>
                <th style="width: 80px;">状态</th>
                <th style="width: 160px;">创建时间</th>
                <th style="width: 120px;">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in accounts" :key="account.id">
                <td>{{ account.id }}</td>
                <td>{{ account.username }}</td>
                <td>{{ account.role }}</td>
                <td>
                  <span :class="['status-badge', account.status === 'active' ? 'status-active' : 'status-inactive']">
                    {{ account.status === 'active' ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>{{ account.createTime }}</td>
                <td class="action-buttons">
                  <button class="btn-edit" @click="editAccount(account)">
                    <span>编辑</span>
                  </button>
                  <button :class="['btn-toggle', account.status === 'active' ? 'btn-disable' : 'btn-enable']"
                    @click="toggleAccountStatus(account)">
                    <span>{{ account.status === 'active' ? '停用' : '启用' }}</span>
                  </button>
                  <button class="btn-reset-pwd" @click="resetPassword(account)">
                    <span>重置密码</span>
                  </button>
                  <button class="btn-delete" @click="deleteAccount(account.id)">
                    <span>删除</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 角色管理 -->
      <div v-show="activeTab === 'role'" class="tab-panel">
        <div class="panel-header">
          <div class="panel-title">
            <span class="title-icon">🎭</span>
            <h2>角色管理</h2>
          </div>
          <button class="btn-add" @click="showAddRoleModal = true">
            <span class="btn-icon">+</span>
            <span>添加角色</span>
          </button>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">ID</th>
                <th style="width: 120px;">角色名称</th>
                <th style="width: 300px;">权限</th>
                <th style="width: 160px;">描述</th>
                <th style="width: 160px;">创建时间</th>
                <th style="width: 120px;">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="role in roles" :key="role.id">
                <td>{{ role.id }}</td>
                <td>{{ role.name }}</td>
                <td>
                  <div class="permission-tree-preview">
                    <div v-for="module in permissionTree" :key="module.id" class="module-item">
                      <span class="module-name-small">{{ module.label }}</span>
                      <span class="module-perms">
                        <span v-for="child in module.children" :key="child.id" class="perm-badge"
                          :class="{ active: role.permissions.includes(child.id) }">{{ child.label }}</span>
                      </span>
                    </div>
                  </div>
                </td>
                <td>{{ role.description }}</td>
                <td>{{ role.createTime }}</td>
                <td>
                  <div>
                    <button class="btn-edit" @click="editRole(role)">
                      <span>编辑</span>
                    </button>
                    <button class="btn-delete" @click="deleteRole(role.id)">
                      <span>删除</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 操作日志 -->
      <div v-show="activeTab === 'log'" class="tab-panel">
        <div class="panel-header">
          <div class="panel-title">
            <span class="title-icon">📋</span>
            <h2>操作日志</h2>
          </div>
          <div class="search-bar">
            <div class="search-input-box">
              <input type="text" v-model="logSearch" placeholder="搜索操作描述..." class="search-input" />
              <span class="search-icon">🔍</span>
            </div>
            <div class="search-select-box">
              <select v-model="logUser" class="search-select">
                <option value="">全部操作人</option>
                <option value="admin">admin</option>
                <option value="manager">manager</option>
                <option value="user1">user1</option>
                <option value="audit_user">audit_user</option>
              </select>
              <span class="select-arrow">▼</span>
            </div>
            <div class="search-select-box">
              <select v-model="logType" class="search-select">
                <option value="">全部类型</option>
                <option value="login">登录</option>
                <option value="logout">退出</option>
                <option value="add">新增</option>
                <option value="edit">编辑</option>
                <option value="delete">删除</option>
                <option value="permission_add">权限添加</option>
                <option value="permission_remove">权限移除</option>
                <option value="permission_modify">权限修改</option>
                <option value="role_assign">角色分配</option>
                <option value="role_create">角色创建</option>
                <option value="role_delete">角色删除</option>
                <option value="system_config">系统配置</option>
              </select>
              <span class="select-arrow">▼</span>
            </div>
            <div class="search-input-box">
              <input type="date" v-model="logStartDate" class="search-input" placeholder="开始日期" />
            </div>
            <div class="search-input-box">
              <input type="date" v-model="logEndDate" class="search-input" placeholder="结束日期" />
            </div>
            <button class="btn-reset" @click="resetFilters">重置</button>
          </div>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">ID</th>
                <th style="width: 100px;">操作用户</th>
                <th style="width: 100px;">操作类型</th>
                <th style="width: 100px;">所属模块</th>
                <th style="width: 100px;">操作目标</th>
                <th style="width: 200px;">操作描述</th>
                <th style="width: 160px;">操作前状态</th>
                <th style="width: 160px;">操作后状态</th>
                <th style="width: 160px;">操作时间</th>
                <th style="width: 120px;">IP地址</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in filteredLogs" :key="log.id">
                <td>{{ log.id }}</td>
                <td>{{ log.user }}</td>
                <td>
                  <span :class="['log-type', log.type]">{{ getLogTypeName(log.type) }}</span>
                </td>
                <td>{{ log.module || '-' }}</td>
                <td>{{ log.target || '-' }}</td>
                <td>{{ log.description }}</td>
                <td>
                  <div class="state-cell" :class="{ 'state-change': log.beforeState }">
                    <span v-if="log.beforeState" class="state-value">{{ log.beforeState }}</span>
                    <span v-else class="state-empty">-</span>
                  </div>
                </td>
                <td>
                  <div class="state-cell" :class="{ 'state-change': log.afterState }">
                    <span v-if="log.afterState" class="state-value">{{ log.afterState }}</span>
                    <span v-else class="state-empty">-</span>
                  </div>
                </td>
                <td>{{ log.time }}</td>
                <td>{{ log.ip }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 添加账号弹窗 -->
    <div class="add-account-modal" v-if="showAddAccountModal">
      <div class="modal-overlay" @click="closeAddAccountModal"></div>
      <div class="modal-content account-modal">
        <div class="modal-header">
          <div class="modal-title">{{ editingAccount ? '编辑账号' : '添加账号' }}</div>
          <button class="modal-close" @click="closeAddAccountModal">
            <span>×</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">用户名：</label>
              <input type="text" class="form-input" v-model="accountForm.username" placeholder="请输入用户名" />
            </div>
            <div class="form-item">
              <label class="form-label">密码：</label>
              <input type="password" class="form-input" v-model="accountForm.password" placeholder="请输入密码" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">账号等级：</label>
              <select class="form-select" v-model="accountForm.level">
                <option value="county">县域级</option>
                <option value="scenic">景区级</option>
                <option value="enterprise">企业级</option>
              </select>
            </div>
            <div class="form-item">
              <label class="form-label">角色：</label>
              <select class="form-select" v-model="accountForm.role">
                <option value="超级管理员">超级管理员</option>
                <option value="管理员">管理员</option>
                <option value="普通用户">普通用户</option>
                <option value="访客">访客</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">状态：</label>
              <select class="form-select" v-model="accountForm.status">
                <option value="active">启用</option>
                <option value="inactive">禁用</option>
              </select>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn-cancel" @click="closeAddAccountModal">取消</button>
            <button class="btn-confirm" @click="saveAccount">确定</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑角色弹窗 -->
    <div class="add-role-modal" v-if="showAddRoleModal">
      <div class="modal-overlay" @click="closeAddRoleModal"></div>
      <div class="modal-content role-modal">
        <div class="modal-header">
          <div class="modal-title">{{ editingRole ? '编辑角色' : '添加角色' }}</div>
          <button class="modal-close" @click="closeAddRoleModal">
            <span>×</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">角色名称：</label>
              <input type="text" class="form-input" v-model="roleForm.name" placeholder="请输入角色名称" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">角色描述：</label>
              <textarea class="form-textarea" v-model="roleForm.description" placeholder="请输入角色描述"></textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label class="form-label">权限配置：</label>
              <div class="permission-tree-container">
                <div v-for="module in permissionTree" :key="module.id" class="tree-node">
                  <div class="tree-parent" @click="toggleModuleSelection(module)">
                    <span class="tree-icon">{{ module.expanded ? '▼' : '▶' }}</span>
                    <input type="checkbox" :checked="isModuleChecked(module)"
                      :indeterminate="isModuleIndeterminate(module)" @change.stop="toggleModuleSelection(module)"
                      class="parent-checkbox" />
                    <span class="tree-label">{{ module.label }}</span>
                  </div>
                  <div v-show="module.expanded" class="tree-children">
                    <label v-for="child in module.children" :key="child.id" class="tree-child">
                      <input type="checkbox" :checked="roleForm.permissions.includes(child.id)"
                        @change="togglePermission(child.id)" />
                      <span class="child-label">{{ child.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn-cancel" @click="closeAddRoleModal">取消</button>
            <button class="btn-confirm" @click="saveRole">确定</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认操作弹窗 -->
    <div class="confirm-modal" v-if="showConfirmModal">
      <div class="modal-overlay" @click="closeConfirmModal"></div>
      <div class="modal-content confirm-modal-content">
        <div class="modal-header">
          <div class="modal-title">确认操作</div>
          <button class="modal-close" @click="closeConfirmModal">
            <span>×</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="confirm-message">{{ confirmMessage }}</div>
        </div>
        <div class="form-actions">
          <button class="btn-cancel" @click="closeConfirmModal">取消</button>
          <button class="btn-confirm btn-danger" @click="confirmAction">确定</button>
        </div>
      </div>
    </div>

    <!-- 密码重置弹窗 -->
    <div class="reset-pwd-modal" v-if="showResetPwdModal">
      <div class="modal-overlay" @click="closeResetPwdModal"></div>
      <div class="modal-content reset-pwd-modal-content">
        <div class="modal-header">
          <div class="modal-title">重置密码</div>
          <button class="modal-close" @click="closeResetPwdModal">
            <span>×</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="reset-pwd-info">
            <span>账号：</span>
            <span class="reset-pwd-username">{{ resetPwdAccount && resetPwdAccount.username }}</span>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">新密码：</label>
              <input type="password" class="form-input" v-model="newPassword" placeholder="请输入新密码" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">确认密码：</label>
              <input type="password" class="form-input" v-model="confirmPassword" placeholder="请再次输入密码" />
            </div>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn-cancel" @click="closeResetPwdModal">取消</button>
          <button class="btn-confirm" @click="doResetPassword">确定</button>
        </div>
      </div>
    </div>

    <!-- 提示弹窗 -->
    <div class="tip-modal" v-if="showTipModal">
      <div class="modal-overlay" @click="closeTipModal"></div>
      <div class="modal-content tip-modal-content">
        <div class="modal-header">
          <div class="modal-title">提示</div>
          <button class="modal-close" @click="closeTipModal">
            <span>×</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="tip-message">{{ tipMessage }}</div>
        </div>
        <div class="form-actions">
          <button class="btn-confirm" @click="closeTipModal">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'System',
  data() {
    return {
      activeTab: 'account',
      logSearch: '',
      logType: '',
      logUser: '',
      logStartDate: '',
      logEndDate: '',
      showAddAccountModal: false,
      showAddRoleModal: false,
      editingRole: null,
      roleForm: {
        name: '',
        description: '',
        permissions: []
      },
      editingAccount: null,
      accountForm: {
        username: '',
        password: '',
        level: 'county',
        role: '普通用户',
        status: 'active'
      },
      // 弹窗状态
      showConfirmModal: false,
      confirmMessage: '',
      confirmCallback: null,
      showResetPwdModal: false,
      resetPwdAccount: null,
      newPassword: '',
      confirmPassword: '',
      showTipModal: false,
      tipMessage: '',
      accounts: [
        { id: 1, username: 'admin', role: '超级管理员', status: 'active', createTime: '2026-06-09 10:30:00' },
        { id: 2, username: 'manager', role: '管理员', status: 'active', createTime: '2026-06-09 14:20:00' },
        { id: 3, username: 'user1', role: '普通用户', status: 'active', createTime: '2026-06-09 09:15:00' },
        { id: 4, username: 'user2', role: '普通用户', status: 'inactive', createTime: '2026-06-09 16:45:00' },
        { id: 5, username: 'guest', role: '访客', status: 'active', createTime: '2026-06-09 11:00:00' }
      ],
      roles: [
        {
          id: 1, name: '超级管理员', description: '系统最高权限', createTime: '2026-06-09 08:00:00',
          permissions: ['account_view', 'account_edit', 'account_export', 'role_view', 'role_edit', 'role_export', 'data_view', 'data_edit', 'data_export', 'report_view', 'report_edit', 'report_export', 'system_view', 'system_edit', 'system_export', 'log_view', 'log_edit', 'log_export']
        },
        {
          id: 2, name: '管理员', description: '系统管理权限', createTime: '2026-06-09 10:30:00',
          permissions: ['account_view', 'account_edit', 'role_view', 'role_edit', 'data_view', 'data_edit', 'report_view', 'report_export', 'system_view', 'log_view']
        },
        {
          id: 3, name: '普通用户', description: '基础操作权限', createTime: '2026-06-09 14:00:00',
          permissions: ['data_view', 'report_view', 'report_export', 'log_view']
        },
        {
          id: 4, name: '访客', description: '只读权限', createTime: '2026-06-09 09:20:00',
          permissions: ['data_view']
        }
      ],
      permissionTree: [
        {
          id: 'account', label: '账号管理', children: [
            { id: 'account_view', label: '查看', parent: 'account' },
            { id: 'account_edit', label: '编辑', parent: 'account' },
            { id: 'account_export', label: '导出', parent: 'account' }
          ]
        },
        {
          id: 'role', label: '角色管理', children: [
            { id: 'role_view', label: '查看', parent: 'role' },
            { id: 'role_edit', label: '编辑', parent: 'role' },
            { id: 'role_export', label: '导出', parent: 'role' }
          ]
        },
        {
          id: 'data', label: '数据查看', children: [
            { id: 'data_view', label: '查看', parent: 'data' },
            { id: 'data_edit', label: '编辑', parent: 'data' },
            { id: 'data_export', label: '导出', parent: 'data' }
          ]
        },
        {
          id: 'report', label: '报表导出', children: [
            { id: 'report_view', label: '查看', parent: 'report' },
            { id: 'report_edit', label: '编辑', parent: 'report' },
            { id: 'report_export', label: '导出', parent: 'report' }
          ]
        },
        {
          id: 'system', label: '系统设置', children: [
            { id: 'system_view', label: '查看', parent: 'system' },
            { id: 'system_edit', label: '编辑', parent: 'system' },
            { id: 'system_export', label: '导出', parent: 'system' }
          ]
        },
        {
          id: 'log', label: '日志查看', children: [
            { id: 'log_view', label: '查看', parent: 'log' },
            { id: 'log_edit', label: '编辑', parent: 'log' },
            { id: 'log_export', label: '导出', parent: 'log' }
          ]
        }
      ],
      logs: [
        { id: 1, user: 'admin', type: 'login', description: '用户登录系统', time: '2026-06-09 09:00:00', ip: '192.168.1.100', target: '系统', module: '系统登录', beforeState: '未登录', afterState: '已登录' },
        { id: 2, user: 'admin', type: 'role_create', description: '创建角色：审计员', time: '2026-06-09 09:10:00', ip: '192.168.1.100', target: '审计员', module: '角色管理', beforeState: '角色不存在', afterState: '角色已创建' },
        { id: 3, user: 'admin', type: 'permission_add', description: '为角色 审计员 添加权限：日志查看', time: '2026-06-09 09:12:00', ip: '192.168.1.100', target: '审计员', module: '权限管理', beforeState: '无权限', afterState: '日志查看' },
        { id: 4, user: 'admin', type: 'add', description: '新增账号 audit_user', time: '2026-06-09 09:15:00', ip: '192.168.1.100', target: 'audit_user', module: '账号管理', beforeState: '账号不存在', afterState: '账号已创建' },
        { id: 5, user: 'admin', type: 'role_assign', description: '为账号 audit_user 分配角色：审计员', time: '2026-06-09 09:18:00', ip: '192.168.1.100', target: 'audit_user', module: '角色管理', beforeState: '无角色', afterState: '审计员' },
        { id: 6, user: 'manager', type: 'edit', description: '编辑角色 普通用户', time: '2026-06-09 10:30:00', ip: '192.168.1.101', target: '普通用户', module: '角色管理', beforeState: '旧描述', afterState: '已更新' },
        { id: 7, user: 'manager', type: 'permission_modify', description: '修改角色 普通用户 权限：移除报表导出', time: '2026-06-09 10:35:00', ip: '192.168.1.101', target: '普通用户', module: '权限管理', beforeState: '数据查看、报表导出', afterState: '数据查看' },
        { id: 8, user: 'user1', type: 'login', description: '用户登录系统', time: '2026-06-09 11:00:00', ip: '192.168.1.102', target: '系统', module: '系统登录', beforeState: '未登录', afterState: '已登录' },
        { id: 9, user: 'admin', type: 'system_config', description: '修改系统配置：日志保留天数设为30天', time: '2026-06-09 13:00:00', ip: '192.168.1.100', target: '系统配置', module: '系统设置', beforeState: '日志保留7天', afterState: '日志保留30天' },
        { id: 10, user: 'admin', type: 'delete', description: '删除账号 user2', time: '2026-06-09 14:00:00', ip: '192.168.1.100', target: 'user2', module: '账号管理', beforeState: '账号存在（禁用）', afterState: '账号已删除' },
        { id: 11, user: 'admin', type: 'role_delete', description: '删除角色：临时访客', time: '2026-06-09 14:30:00', ip: '192.168.1.100', target: '临时访客', module: '角色管理', beforeState: '角色存在', afterState: '角色已删除' },
        { id: 12, user: 'admin', type: 'permission_remove', description: '从角色 管理员 移除权限：系统设置', time: '2026-06-09 15:00:00', ip: '192.168.1.100', target: '管理员', module: '权限管理', beforeState: '全部管理权限', afterState: '账号管理、角色管理' },
        { id: 13, user: 'user1', type: 'logout', description: '用户退出系统', time: '2026-06-09 17:30:00', ip: '192.168.1.102', target: '系统', module: '系统登录', beforeState: '已登录', afterState: '已退出' },
        { id: 14, user: 'audit_user', type: 'login', description: '用户登录系统', time: '2026-06-09 18:00:00', ip: '192.168.1.103', target: '系统', module: '系统登录', beforeState: '未登录', afterState: '已登录' },
        { id: 15, user: 'audit_user', type: 'logout', description: '用户退出系统', time: '2026-06-09 18:30:00', ip: '192.168.1.103', target: '系统', module: '系统登录', beforeState: '已登录', afterState: '已退出' }
      ]
    }
  },
  computed: {
    filteredLogs() {
      return this.logs.filter(log => {
        const matchSearch = !this.logSearch ||
          log.user.includes(this.logSearch) ||
          log.description.includes(this.logSearch)
        const matchType = !this.logType || log.type === this.logType
        const matchUser = !this.logUser || log.user === this.logUser
        const matchStartDate = !this.logStartDate || log.time >= this.logStartDate
        const matchEndDate = !this.logEndDate || log.time <= this.logEndDate + ' 23:59:59'
        return matchSearch && matchType && matchUser && matchStartDate && matchEndDate
      })
    }
  },
  methods: {
    getLogTypeName(type) {
      const types = {
        login: '登录',
        logout: '退出',
        add: '新增',
        edit: '编辑',
        delete: '删除',
        permission_add: '权限添加',
        permission_remove: '权限移除',
        permission_modify: '权限修改',
        role_assign: '角色分配',
        role_create: '角色创建',
        role_delete: '角色删除',
        system_config: '系统配置'
      }
      return types[type] || type
    },
    resetFilters() {
      this.logSearch = ''
      this.logType = ''
      this.logUser = ''
      this.logStartDate = ''
      this.logEndDate = ''
    },
    editAccount(account) {
      this.editingAccount = account
      this.accountForm = {
        username: account.username,
        password: '',
        level: account.level || 'county',
        role: account.role,
        status: account.status
      }
      this.showAddAccountModal = true
    },
    closeAddAccountModal() {
      this.showAddAccountModal = false
      this.editingAccount = null
      this.accountForm = {
        username: '',
        password: '',
        level: 'county',
        role: '普通用户',
        status: 'active'
      }
    },
    saveAccount() {
      if (!this.accountForm.username) {
        this.showTipMessage('请输入用户名')
        return
      }
      if (!this.accountForm.password && !this.editingAccount) {
        this.showTipMessage('请输入密码')
        return
      }

      if (this.editingAccount) {
        const index = this.accounts.findIndex(a => a.id === this.editingAccount.id)
        if (index !== -1) {
          this.accounts[index] = {
            ...this.accounts[index],
            username: this.accountForm.username,
            role: this.accountForm.role,
            level: this.accountForm.level,
            status: this.accountForm.status
          }
          if (this.accountForm.password) {
            this.accounts[index].password = this.accountForm.password
          }
        }
      } else {
        const newId = Math.max(...this.accounts.map(a => a.id)) + 1
        this.accounts.push({
          id: newId,
          username: this.accountForm.username,
          password: this.accountForm.password,
          level: this.accountForm.level,
          role: this.accountForm.role,
          status: this.accountForm.status,
          createTime: new Date().toLocaleString('zh-CN')
        })
      }

      this.closeAddAccountModal()
      this.showTipMessage(this.editingAccount ? '编辑成功' : '添加成功')
    },
    deleteAccount(id) {
      const account = this.accounts.find(a => a.id === id)
      this.showConfirmModalFn(`确定要删除账号 "${account && account.username}" 吗？`, () => {
        this.accounts = this.accounts.filter(a => a.id !== id)
        this.showTipMessage('删除成功')
      })
    },
    toggleAccountStatus(account) {
      const action = account.status === 'active' ? '停用' : '启用'
      this.showConfirmModalFn(`确定要${action}账号 "${account.username}" 吗？`, () => {
        account.status = account.status === 'active' ? 'inactive' : 'active'
        this.showTipMessage(`${action}成功`)
      })
    },
    resetPassword(account) {
      this.resetPwdAccount = account
      this.newPassword = ''
      this.confirmPassword = ''
      this.showResetPwdModal = true
    },
    doResetPassword() {
      if (!this.newPassword.trim()) {
        this.showTipMessage('请输入新密码')
        return
      }
      if (this.newPassword !== this.confirmPassword) {
        this.showTipMessage('两次输入的密码不一致')
        return
      }

      if (this.resetPwdAccount) {
        this.resetPwdAccount.password = this.newPassword
        this.closeResetPwdModal()
        this.showTipMessage('密码重置成功')
      }
    },
    showConfirmModalFn(message, callback) {
      this.confirmMessage = message
      this.confirmCallback = callback
      this.showConfirmModal = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.confirmMessage = ''
      this.confirmCallback = null
    },
    confirmAction() {
      if (this.confirmCallback) {
        this.confirmCallback()
      }
      this.closeConfirmModal()
    },
    closeResetPwdModal() {
      this.showResetPwdModal = false
      this.resetPwdAccount = null
      this.newPassword = ''
      this.confirmPassword = ''
    },
    showTipMessage(message) {
      this.tipMessage = message
      this.showTipModal = true
    },
    closeTipModal() {
      this.showTipModal = false
      this.tipMessage = ''
    },
    editRole(role) {
      this.editingRole = role
      this.roleForm = {
        name: role.name,
        description: role.description,
        permissions: JSON.parse(JSON.stringify(role.permissions))
      }
      this.showAddRoleModal = true
    },
    deleteRole(id) {
      const role = this.roles.find(r => r.id === id)
      this.showConfirmModalFn(`确定要删除角色 "${role && role.name}" 吗？`, () => {
        this.roles = this.roles.filter(r => r.id !== id)
        this.showTipMessage('删除成功')
      })
    },
    closeAddRoleModal() {
      this.showAddRoleModal = false
      this.editingRole = null
      this.roleForm = {
        name: '',
        description: '',
        permissions: []
      }
      this.permissionTree.forEach(module => {
        module.expanded = false
      })
    },
    saveRole() {
      if (!this.roleForm.name.trim()) {
        this.showTipMessage('请输入角色名称')
        return
      }

      if (this.editingRole) {
        const index = this.roles.findIndex(r => r.id === this.editingRole.id)
        if (index !== -1) {
          this.roles[index] = {
            ...this.roles[index],
            name: this.roleForm.name,
            description: this.roleForm.description,
            permissions: [...this.roleForm.permissions]
          }
        }
        this.showTipMessage('编辑成功')
      } else {
        const newId = Math.max(...this.roles.map(r => r.id)) + 1
        this.roles.push({
          id: newId,
          name: this.roleForm.name,
          description: this.roleForm.description,
          permissions: [...this.roleForm.permissions],
          createTime: new Date().toLocaleString('zh-CN')
        })
        this.showTipMessage('添加成功')
      }

      this.closeAddRoleModal()
    },
    togglePermission(permId) {
      const index = this.roleForm.permissions.indexOf(permId)
      if (index > -1) {
        this.roleForm.permissions.splice(index, 1)
      } else {
        this.roleForm.permissions.push(permId)
      }
    },
    toggleModuleSelection(module) {
      module.expanded = !module.expanded
      const childIds = module.children.map(c => c.id)
      const allChecked = childIds.every(id => this.roleForm.permissions.includes(id))

      if (allChecked) {
        childIds.forEach(id => {
          const idx = this.roleForm.permissions.indexOf(id)
          if (idx > -1) this.roleForm.permissions.splice(idx, 1)
        })
      } else {
        childIds.forEach(id => {
          if (!this.roleForm.permissions.includes(id)) {
            this.roleForm.permissions.push(id)
          }
        })
      }
    },
    isModuleChecked(module) {
      return module.children.every(child => this.roleForm.permissions.includes(child.id))
    },
    isModuleIndeterminate(module) {
      const checkedCount = module.children.filter(child => this.roleForm.permissions.includes(child.id)).length
      return checkedCount > 0 && checkedCount < module.children.length
    }
  }
}
</script>

<style scoped>
.system-container {
  min-height: calc(100vh - 54px);
  background: linear-gradient(135deg, #0a1628 0%, #0d1b2a 50%, #1b263b 100%);
  padding: 20px;
  box-sizing: border-box;
}

.tab-container {
  display: flex;
  background: rgba(3, 10, 69, 0.95);
  border: 1px solid rgba(80, 220, 255, 0.3);
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 20px;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: rgba(96, 98, 102, 0.7);
  font-size: 14px;
  font-weight: 500;
}

.tab-item:hover {
  background: rgba(76, 165, 255, 0.1);
  color: #606266;
}

.tab-item.active {
  background: linear-gradient(135deg, #4cd9ff 0%, #00b4d8 100%);
  color: #141439;
  font-weight: 600;
}

.tab-icon {
  font-size: 16px;
}

.tab-text {
  font-weight: 500;
}

.tab-content {
  background: rgba(3, 10, 69, 0.95);
  border: 1px solid rgba(80, 220, 255, 0.3);
  border-radius: 8px;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tab-panel {
  animation: fadeIn 0.3s ease;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(76, 165, 255, 0.2);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-title h2 {
  font-size: 18px;
  font-weight: 600;
  color: #606266;
  margin: 0;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(11, 27, 88, 0.72);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 4px;
  color: rgba(96, 98, 102, 0.86);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-reset {
  padding: 8px 16px;
  background: rgba(11, 27, 88, 0.72);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 4px;
  color: rgba(96, 98, 102, 0.86);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-reset:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-add:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-icon {
  font-size: 18px;
  font-weight: 300;
}

.table-container {
  max-height: calc(100vh - 280px);
  overflow: auto;
  background: rgba(3, 10, 69, 0.95);
  border: 1px solid rgba(80, 220, 255, 0.3);
  border-radius: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(76, 165, 255, 0.1);
}

.data-table th {
  position: sticky;
  top: 0;
  background: rgba(11, 27, 88, 0.72);
  font-weight: 600;
  color: rgba(96, 98, 102, 0.9);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.data-table td {
  color: rgba(96, 98, 102, 0.8);
}

.data-table tr:hover {
  background: rgba(76, 165, 255, 0.08);
}

.data-table tr:last-child td {
  border-bottom: none;
}

.status-badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.status-inactive {
  background: rgba(107, 114, 128, 0.2);
  color: #9ca3af;
  border: 1px solid rgba(107, 114, 128, 0.3);
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

td.action-buttons {
  white-space: nowrap;
}

.btn-edit,
.btn-delete,
.btn-toggle,
.btn-reset-pwd {
  padding: 6px 16px;
  background: rgba(11, 27, 88, 0.72);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: rgba(96, 98, 102, 0.86);
  transition: all 0.3s;
}

.btn-edit:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-delete:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-toggle.btn-disable:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-toggle.btn-enable:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-reset-pwd:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.permission-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.permission-tag {
  padding: 3px 10px;
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

/* 角色弹窗样式 */
.role-modal {
  width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.role-modal .modal-body {
  padding: 0;
}

.role-modal .form-item.full-width {
  width: 100%;
}

.role-modal .form-textarea {
  width: 100%;
  height: 60px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 4px;
  color: #606266;
  font-size: 13px;
  resize: none;
  box-sizing: border-box;
}

.role-modal .form-textarea::placeholder {
  color: rgba(96, 98, 102, 0.4);
}

/* 权限树容器 */
.permission-tree-container {
  max-height: 350px;
  overflow-y: auto;
  margin-top: 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 8px;
}

.tree-node {
  margin-bottom: 4px;
}

.tree-parent {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.tree-parent:hover {
  background: rgba(76, 165, 255, 0.1);
}

.tree-icon {
  font-size: 10px;
  color: rgba(96, 98, 102, 0.5);
  width: 14px;
  text-align: center;
}

.parent-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.tree-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(96, 98, 102, 0.9);
}

.tree-children {
  padding-left: 32px;
}

.tree-child {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.tree-child:hover {
  background: rgba(76, 165, 255, 0.08);
}

.tree-child input {
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.child-label {
  font-size: 12px;
  color: rgba(96, 98, 102, 0.7);
}

/* 权限预览样式 */
.permission-tree-preview {
  max-height: 120px;
  overflow-y: auto;
}

.module-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.module-name-small {
  font-size: 11px;
  color: rgba(96, 98, 102, 0.5);
  width: 60px;
  flex-shrink: 0;
}

.module-perms {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.perm-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.3);
  color: rgba(96, 98, 102, 0.4);
  border-radius: 3px;
}

.perm-badge.active {
  background: rgba(76, 165, 255, 0.2);
  color: #4cd9ff;
}

.search-bar {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-input-box {
  position: relative;
}

.search-input {
  padding: 8px 36px 8px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 6px;
  color: #606266;
  font-size: 13px;
  width: 220px;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #4cd9ff;
}

.search-input::placeholder {
  color: rgba(96, 98, 102, 0.4);
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
}

.search-select-box {
  position: relative;
}

.search-select {
  padding: 8px 32px 8px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 6px;
  color: #606266;
  font-size: 13px;
  cursor: pointer;
  appearance: none;
  transition: border-color 0.3s ease;
}

.search-select:focus {
  outline: none;
  border-color: #4cd9ff;
}

.search-select option {
  background: #1a1a2e;
  color: #606266;
}

.select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(96, 98, 102, 0.6);
  font-size: 10px;
  pointer-events: none;
}

.log-type {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.log-type.login {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.log-type.add {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.log-type.edit {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.log-type.delete {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.log-type.logout {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
  border: 1px solid rgba(156, 163, 175, 0.3);
}

.log-type.permission_add {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.log-type.permission_remove {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.log-type.permission_modify {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.log-type.role_assign {
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.log-type.role_create {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.log-type.role_delete {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.log-type.system_config {
  background: rgba(6, 182, 212, 0.2);
  color: #22d3ee;
  border: 1px solid rgba(6, 182, 212, 0.3);
}

.state-cell {
  padding: 6px 10px;
  border-radius: 4px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state-cell.state-change {
  background: rgba(11, 27, 88, 0.7);
  border: 1px solid rgba(76, 165, 255, 0.2);
}

.state-value {
  font-size: 12px;
  color: rgba(96, 98, 102, 0.9);
}

.state-empty {
  font-size: 12px;
  color: rgba(96, 98, 102, 0.3);
}

/* 添加账号弹窗 */
.add-account-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

/* 添加角色弹窗 */
.add-role-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(2, 8, 30, 0.72);
  backdrop-filter: blur(4px);
}

.modal-content {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80%;
  max-width: 900px;
  max-height: 85vh;
  transform: translate(-50%, -50%);
  padding: 18px 18px 16px;
  box-sizing: border-box;
  background: rgba(3, 10, 69, 0.95);
  border: 1px solid rgba(80, 220, 255, 0.3);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(78, 240, 228, 0.2);
  z-index: 1001;
}

.modal-content.account-modal {
  max-width: 600px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #606266;
}

.modal-close {
  width: 32px;
  height: 32px;
  line-height: 28px;
  text-align: center;
  color: rgba(96, 98, 102, 0.72);
  font-size: 28px;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 0;
}

.form-row {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
}

.form-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  color: rgba(96, 98, 102, 0.7);
}

.form-input,
.form-select {
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 6px;
  color: #606266;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;
}

.form-input:focus,
.form-select:focus {
  border-color: #4cd9ff;
}

.form-input::placeholder {
  color: rgba(96, 98, 102, 0.4);
}

.form-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.6)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
}

.form-select option {
  background: #1a1a2e;
  color: #606266;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding: 16px 20px;
  border-top: 1px solid rgba(80, 220, 255, 0.2);
}

.btn-cancel,
.btn-confirm {
  padding: 8px 0;
  flex: 1;
  background: rgba(11, 27, 88, 0.72);
  border: 1px solid rgba(76, 165, 255, 0.3);
  border-radius: 4px;
  font-size: 12px;
  color: rgba(96, 98, 102, 0.86);
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-confirm:hover {
  border-color: #4cd9ff;
  background: rgba(76, 217, 255, 0.1);
}

.btn-confirm.btn-danger {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
}

.btn-confirm.btn-danger:hover {
  background: linear-gradient(135deg, #fca5a5 0%, #f87171 100%);
}

/* 确认弹窗 */
.confirm-modal {
  position: fixed;
  inset: 0;
  z-index: 1001;
}

.modal-content.confirm-modal-content {
  max-width: 450px;
}

.confirm-message {
  font-size: 14px;
  color: rgba(96, 98, 102, 0.9);
  text-align: center;
  padding: 20px 0;
  line-height: 1.6;
}

/* 密码重置弹窗 */
.reset-pwd-modal {
  position: fixed;
  inset: 0;
  z-index: 1002;
}

.modal-content.reset-pwd-modal-content {
  max-width: 500px;
}

.reset-pwd-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(80, 220, 255, 0.2);
  font-size: 14px;
  color: rgba(96, 98, 102, 0.7);
}

.reset-pwd-username {
  color: #4cd9ff;
  font-weight: 600;
}

/* 提示弹窗 */
.tip-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1003;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content.tip-modal-content {
  max-width: 400px;
}

.tip-message {
  font-size: 14px;
  color: rgba(96, 98, 102, 0.9);
  text-align: center;
  padding: 20px 0;
  line-height: 1.6;
}

/* B 公司白色系系统管理覆盖 */
.system-container {
  background: linear-gradient(180deg, #f7fafc 0%, #eef4f8 100%);
  color: #22313f;
}

.tab-container,
.tab-content,
.table-container,
.modal-content {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(34, 49, 63, 0.08);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(34, 49, 63, 0.1);
  color: #22313f;
}

.tab-item {
  color: #526173;
}

.tab-item:hover {
  background: rgba(47, 128, 237, 0.08);
  color: #2f80ed;
}

.tab-item.active {
  background: #2f80ed;
  color: #606266;
  box-shadow: 0 8px 18px rgba(47, 128, 237, 0.18);
}

.panel-header,
.modal-header,
.form-actions,
.reset-pwd-info {
  border-color: rgba(34, 49, 63, 0.08);
}

.panel-title h2,
.modal-title,
.confirm-message,
.tip-message,
.reset-pwd-info,
.data-table td,
.module-name-small,
.state-value {
  color: #22313f;
}

.data-table thead,
.permission-tree-preview,
.state-cell,
.search-input-box,
.search-select-box,
.form-row {
  background: rgba(47, 128, 237, 0.04);
  border-color: rgba(34, 49, 63, 0.08);
}

.data-table th {
  background: #eaf2fb;
  color: #22313f;
  border-color: rgba(47, 128, 237, 0.12);
}

.data-table td {
  border-color: rgba(34, 49, 63, 0.08);
}

.data-table tbody tr:hover {
  background: rgba(47, 128, 237, 0.06);
}

.btn-add,
.btn-reset,
.btn-edit,
.btn-toggle,
.btn-reset-pwd,
.btn-delete,
.btn-cancel,
.btn-confirm {
  background: #f6f9fc;
  border: 1px solid rgba(34, 49, 63, 0.12);
  border-radius: 6px;
  color: #526173;
}

.btn-add,
.btn-confirm {
  background: #2f80ed;
  border-color: #2f80ed;
  color: #606266;
  box-shadow: 0 8px 18px rgba(47, 128, 237, 0.18);
}

.btn-edit {
  color: #2f80ed;
  background: rgba(47, 128, 237, 0.08);
}

.btn-delete,
.btn-disable,
.btn-confirm.btn-danger {
  color: #c92a2a;
  background: rgba(250, 82, 82, 0.12);
  border-color: rgba(250, 82, 82, 0.22);
}

.btn-enable,
.status-active {
  color: #0f766e;
  background: rgba(18, 184, 134, 0.12);
  border-color: rgba(18, 184, 134, 0.22);
}

.status-inactive,
.state-empty {
  color: #8a98a8;
}

.perm-badge,
.status-badge,
.log-type {
  border-radius: 6px;
}

.perm-badge.active {
  background: rgba(47, 128, 237, 0.12);
  color: #2f80ed;
  border-color: rgba(47, 128, 237, 0.22);
}

.search-input,
.search-select,
.form-input,
.form-select {
  background-color: #fff;
  border: 1px solid rgba(34, 49, 63, 0.14);
  border-radius: 6px;
  color: #22313f;
}

.search-input::placeholder,
.form-input::placeholder {
  color: #8a98a8;
}

.search-select,
.form-select {
  background-image: none;
}

.search-select option,
.form-select option {
  background: #fff;
  color: #22313f;
}

.modal-overlay {
  background: rgba(34, 49, 63, 0.26);
}

.modal-close {
  background: #f6f9fc;
  color: #526173;
  border-radius: 6px;
}

.reset-pwd-username {
  color: #2f80ed;
}

/* 现代后台 UI 视觉修正 */
.system-container {
  --ui-bg: #f5f7fa;
  --ui-surface: #ffffff;
  --ui-surface-soft: #f9fafc;
  --ui-border: #dcdfe6;
  --ui-border-soft: #ebeef5;
  --ui-text: #1f2937;
  --ui-text-regular: #606266;
  --ui-primary: #409eff;
  --ui-success: #67c23a;
  --ui-danger: #f56c6c;
  --ui-radius: 8px;
  --ui-shadow: 0 8px 24px rgba(31, 45, 61, 0.08);
  background: var(--ui-bg);
  color: var(--ui-text);
}

.tab-container,
.tab-content,
.table-container,
.modal-content {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid var(--ui-border-soft);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow);
  color: var(--ui-text);
}

.panel-title h2,
.modal-title,
.confirm-message,
.tip-message,
.reset-pwd-info,
.data-table td,
.module-name-small,
.state-value {
  color: var(--ui-text);
}

.tab-item,
.search-input,
.search-select,
.form-input,
.form-select,
.modal-close,
.btn-add,
.btn-reset,
.btn-edit,
.btn-toggle,
.btn-reset-pwd,
.btn-delete,
.btn-cancel,
.btn-confirm {
  border-radius: 6px;
}

.tab-item {
  color: var(--ui-text-regular);
}

.tab-item:hover,
.data-table tbody tr:hover {
  background: #ecf5ff;
  color: var(--ui-primary);
}

.tab-item.active,
.btn-add,
.btn-confirm {
  background: var(--ui-primary);
  border-color: var(--ui-primary);
  color: #606266;
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.2);
}

.data-table thead,
.permission-tree-preview,
.state-cell,
.search-input-box,
.search-select-box,
.form-row,
.data-table th {
  background: var(--ui-surface-soft);
  border-color: var(--ui-border-soft);
}

.data-table th {
  color: var(--ui-text);
}

.search-input,
.search-select,
.form-input,
.form-select {
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  color: var(--ui-text);
}

.btn-reset,
.btn-edit,
.btn-toggle,
.btn-reset-pwd,
.btn-delete,
.btn-cancel,
.modal-close {
  background: var(--ui-surface-soft);
  border: 1px solid var(--ui-border);
  color: var(--ui-text-regular);
  box-shadow: none;
}

.btn-edit,
.perm-badge.active,
.reset-pwd-username {
  color: var(--ui-primary);
}

.btn-delete,
.btn-disable,
.btn-confirm.btn-danger {
  color: var(--ui-danger);
  background: #fef0f0;
  border-color: #fde2e2;
}

.btn-enable,
.status-active {
  color: var(--ui-success);
  background: #f0f9eb;
  border-color: #e1f3d8;
}

.modal-overlay {
  background: rgba(31, 45, 61, 0.28);
}

/* 内容排版与文字可读性修正 */
.system-container {
  padding: 24px;
}

.tab-container {
  gap: 8px;
  padding: 6px;
  margin-bottom: 16px;
}

.tab-item {
  min-height: 38px;
  padding: 0 18px;
}

.tab-content,
.table-container,
.modal-content {
  padding: 18px;
  box-sizing: border-box;
}

.tab-content *,
.table-container *,
.modal-content * {
  text-shadow: none;
}

.tab-content,
.tab-content p,
.tab-content span,
.tab-content div,
.table-container,
.table-container p,
.table-container span,
.table-container div,
.modal-content,
.modal-content p,
.modal-content span,
.modal-content div {
  color: var(--ui-text-regular, #606266);
}

.panel-title h2,
.modal-title,
.confirm-message,
.tip-message,
.reset-pwd-info,
.data-table th,
.data-table td,
.module-name-small,
.state-value {
  color: var(--ui-text, #1f2937);
}

.tab-item.active,
.btn-add,
.btn-confirm {
  color: #606266;
}

.panel-header {
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 14px;
}

.search-input-box,
.search-select-box,
.form-row {
  padding: 10px 12px;
  border-radius: 8px;
}

.data-table {
  border-collapse: separate;
  border-spacing: 0;
}

.data-table th,
.data-table td {
  min-height: 36px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--ui-border-soft, #ebeef5);
}

.data-table tr:last-child td {
  border-bottom: 0;
}

.btn-add,
.btn-reset,
.btn-edit,
.btn-toggle,
.btn-reset-pwd,
.btn-delete,
.btn-cancel,
.btn-confirm {
  min-height: 32px;
  padding: 0 14px;
}

.tab-item.active,
.btn-add,
.btn-confirm {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #1d4f91;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/system.vue